import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../config/api';


const PaymentDiagnostic = () => {
  const { currentUser } = useAuth();
  const [diagnosticResults, setDiagnosticResults] = useState({});
  const [isRunning, setIsRunning] = useState(false);

  const runDiagnostic = async () => {
    setIsRunning(true);
    const results = {};

    try {
      // 1. BROWSER ENVIRONMENT CHECKS
      console.log('🔍 === INICIANDO DIAGNÓSTICO DE PAGOS ===');
      
      // Check if incognito/private mode
      results.browserInfo = {
        userAgent: navigator.userAgent,
        cookieEnabled: navigator.cookieEnabled,
        onLine: navigator.onLine,
        platform: navigator.platform,
        language: navigator.language
      };

      // Test Storage Access API availability
      results.storageAccess = {
        hasRequestStorageAccess: 'requestStorageAccess' in document,
        hasRequestStorageAccessFor: 'requestStorageAccessFor' in document,
        hasStorageAccessAPI: 'hasStorageAccess' in document
      };

      // Test various incognito detection methods
      results.incognitoTests = await runIncognitoTests();

      // Test local storage and session storage
      results.storageTests = await runStorageTests();

      // Test third-party cookie support
      results.cookieTests = await runCookieTests();

      // 2. MERCADOPAGO CONFIGURATION CHECK
      if (currentUser) {
        results.mercadopagoConfig = await checkMercadoPagoConfig();
      }

      // 3. NETWORK AND CORS TESTS
      results.networkTests = await runNetworkTests();

      console.log('📊 RESULTADOS COMPLETOS DEL DIAGNÓSTICO:', results);
      setDiagnosticResults(results);

    } catch (error) {
      console.error('❌ Error en diagnóstico:', error);
      results.error = error.message;
      setDiagnosticResults(results);
    } finally {
      setIsRunning(false);
    }
  };

  const runIncognitoTests = async () => {
    const tests = {};

    // Test 1: Storage Quota (Chrome/Edge)
    try {
      if ('storage' in navigator && 'estimate' in navigator.storage) {
        const { usage, quota } = await navigator.storage.estimate();
        tests.storageQuota = {
          usage,
          quota,
          isLikelyIncognito: quota < 120000000
        };
      }
    } catch (e) {
      tests.storageQuota = { error: e.message };
    }

    // Test 2: FileSystem API (Legacy Chrome)
    try {
      tests.fileSystemAPI = await new Promise((resolve) => {
        const fs = window.RequestFileSystem || window.webkitRequestFileSystem;
        if (!fs) {
          resolve({ available: false });
        } else {
          fs(window.TEMPORARY, 100, 
            () => resolve({ available: true, isIncognito: false }),
            () => resolve({ available: true, isIncognito: true })
          );
        }
      });
    } catch (e) {
      tests.fileSystemAPI = { error: e.message };
    }

    // Test 3: WebSQL (Safari)
    try {
      const test = window.openDatabase('test', '1.0', 'test', 1024);
      tests.webSQL = { available: true, isIncognito: false };
    } catch (e) {
      tests.webSQL = { available: true, isIncognito: true, error: e.message };
    }

    // Test 4: IndexedDB
    try {
      tests.indexedDB = await new Promise((resolve) => {
        const request = indexedDB.open('test');
        request.onsuccess = () => resolve({ available: true, isIncognito: false });
        request.onerror = () => resolve({ available: true, isIncognito: true });
      });
    } catch (e) {
      tests.indexedDB = { error: e.message };
    }

    return tests;
  };

  const runStorageTests = async () => {
    const tests = {};

    // Test localStorage
    try {
      localStorage.setItem('diagnostic_test', 'test');
      localStorage.removeItem('diagnostic_test');
      tests.localStorage = { available: true };
    } catch (e) {
      tests.localStorage = { available: false, error: e.message };
    }

    // Test sessionStorage
    try {
      sessionStorage.setItem('diagnostic_test', 'test');
      sessionStorage.removeItem('diagnostic_test');
      tests.sessionStorage = { available: true };
    } catch (e) {
      tests.sessionStorage = { available: false, error: e.message };
    }

    return tests;
  };

  const runCookieTests = async () => {
    const tests = {};

    // Test basic cookie support
    try {
      document.cookie = 'diagnostic_test=test; SameSite=None; Secure';
      const cookieSet = document.cookie.includes('diagnostic_test=test');
      tests.basicCookies = { working: cookieSet };
      
      // Clean up
      document.cookie = 'diagnostic_test=; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    } catch (e) {
      tests.basicCookies = { working: false, error: e.message };
    }

    return tests;
  };

  const checkMercadoPagoConfig = async () => {
    try {
      const token = await currentUser.getIdToken();
      
      // Test backend connectivity
      const response = await fetch(`${API_URL}/api/test/mercadopago-config`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        return await response.json();
      } else {
        return {
          error: `HTTP ${response.status}: ${response.statusText}`,
          backendReachable: true
        };
      }
    } catch (error) {
      return {
        error: error.message,
        backendReachable: false
      };
    }
  };

  const runNetworkTests = async () => {
    const tests = {};

    // Test MercadoPago API accessibility
    try {
      const corsTest = await fetch('https://api.mercadopago.com/v1/payments/search?limit=1', {
        method: 'GET',
        mode: 'no-cors' // This will always succeed but tells us about CORS
      });
      tests.mercadopagoAPI = { reachable: true };
    } catch (e) {
      tests.mercadopagoAPI = { reachable: false, error: e.message };
    }

    return tests;
  };

  const renderDiagnosticResults = () => {
    if (Object.keys(diagnosticResults).length === 0) return null;

    return (
      <div style={{ 
        backgroundColor: '#f5f5f5', 
        padding: '20px', 
        borderRadius: '8px',
        marginTop: '20px',
        fontFamily: 'monospace',
        fontSize: '12px',
        maxHeight: '500px',
        overflow: 'auto'
      }}>
        <h3>📊 Resultados del Diagnóstico</h3>
        <pre>{JSON.stringify(diagnosticResults, null, 2)}</pre>
      </div>
    );
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1>🔍 Diagnóstico de Pagos - MercadoPago</h1>
      
      <div style={{ 
        backgroundColor: '#fff3cd', 
        border: '1px solid #ffeaa7',
        padding: '15px',
        borderRadius: '5px',
        marginBottom: '20px'
      }}>
        <h3>⚠️ Información del Error Reportado:</h3>
        <p><strong>Error principal:</strong> requestStorageAccessFor: Permission denied</p>
        <p><strong>URL:</strong> /checkout/v1/subscription/redirect/...</p>
        <p><strong>Contexto:</strong> Error al intentar realizar pago de suscripción</p>
      </div>

      <button 
        onClick={runDiagnostic}
        disabled={isRunning}
        style={{
          backgroundColor: isRunning ? '#ccc' : '#007bff',
          color: 'white',
          padding: '12px 24px',
          border: 'none',
          borderRadius: '5px',
          cursor: isRunning ? 'not-allowed' : 'pointer',
          fontSize: '16px'
        }}
      >
        {isRunning ? '🔄 Ejecutando Diagnóstico...' : '🚀 Ejecutar Diagnóstico Completo'}
      </button>

      {renderDiagnosticResults()}

      <div style={{ marginTop: '30px', fontSize: '14px' }}>
        <h3>🎯 Qué verifica este diagnóstico:</h3>
        <ul>
          <li><strong>Navegador:</strong> Modo incógnito, soporte de cookies, storage</li>
          <li><strong>Storage Access API:</strong> Disponibilidad y permisos</li>
          <li><strong>MercadoPago:</strong> Configuración del backend y credenciales</li>
          <li><strong>Red:</strong> Conectividad y CORS</li>
          <li><strong>Almacenamiento:</strong> localStorage, sessionStorage, IndexedDB</li>
        </ul>
        
        <div style={{ 
          backgroundColor: '#d1ecf1',
          border: '1px solid #bee5eb',
          padding: '10px',
          borderRadius: '5px',
          marginTop: '15px'
        }}>
          <strong>💡 Instrucciones:</strong>
          <ol>
            <li>Ejecuta el diagnóstico en el navegador donde tienes el problema</li>
            <li>Copia los resultados completos</li>
            <li>Compara resultados entre navegador normal vs incógnito</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default PaymentDiagnostic;